import { Check, Plus, Search, Star, User, X } from "lucide-react";
import type { Customer } from "../../../services/customer.service";

interface CustomerPanelProps {
  selectedCustomer: Customer | null;
  customerSearch: string;
  setCustomerSearch: (value: string) => void;
  customerResults: Customer[];
  showCustomerDropdown: boolean;
  setShowCustomerDropdown: (show: boolean) => void;
  searchingCustomer: boolean;
  isCreatingCustomer: boolean;
  setIsCreatingCustomer: (creating: boolean) => void;
  newName: string;
  setNewName: (name: string) => void;
  newPhone: string;
  setNewPhone: (phone: string) => void;
  selectCustomer: (customer: Customer) => void;
  clearCustomer: () => void;
  handleQuickCreate: () => void;
  processing: boolean;
  setSelectedCustomer: (customer: Customer | null) => void;
}

export function CustomerPanel({
  selectedCustomer,
  customerSearch,
  setCustomerSearch,
  customerResults,
  showCustomerDropdown,
  setShowCustomerDropdown,
  searchingCustomer,
  isCreatingCustomer,
  setIsCreatingCustomer,
  newName,
  setNewName,
  newPhone,
  setNewPhone,
  selectCustomer,
  clearCustomer,
  handleQuickCreate,
  processing,
  setSelectedCustomer,
}: CustomerPanelProps) {
  return (
    <div className="relative space-y-2">
      {selectedCustomer ? (
        <div className="group relative flex items-start gap-3 rounded-xl border border-teal-100 bg-teal-50 p-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
            <User className="h-4 w-4" />
          </div>
          <button
            type="button"
            aria-label="Remove selected customer"
            onClick={clearCustomer}
            className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-lg text-teal-400 transition-colors hover:bg-teal-100 hover:text-teal-700"
          >
            <X className="h-3.5 w-3.5" />
          </button>
          <div className="min-w-0 pr-7">
            <div className="truncate text-sm font-semibold text-teal-950">
              {selectedCustomer.name}
            </div>
            <div className="text-xs text-teal-700">
              {selectedCustomer.phone}
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {selectedCustomer.gstin && (
                <span className="rounded-md border border-teal-200 bg-white/70 px-1.5 py-0.5 text-[10px] font-medium text-teal-700">
                  GSTIN {selectedCustomer.gstin}
                </span>
              )}
              {(selectedCustomer.loyaltyPoints ?? 0) > 0 && (
                <span className="flex items-center gap-1 rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  {selectedCustomer.loyaltyPoints} points
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="relative">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={customerSearch}
              onChange={(e) => {
                setCustomerSearch(e.target.value);
                if (!e.target.value) setSelectedCustomer(null);
              }}
              onFocus={() => {
                if (customerSearch.length >= 2) setShowCustomerDropdown(true);
              }}
              placeholder="Search name or phone"
              aria-label="Search customer by name or phone"
              className="h-10 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 pr-3 text-sm text-slate-800 outline-none transition focus:border-teal-400 focus:bg-white focus:ring-2 focus:ring-teal-500/20"
            />
          </div>

          {showCustomerDropdown && (
            <div className="mt-2 max-h-[280px] overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              {customerResults.map((cust) => (
                <button
                  type="button"
                  key={cust.id}
                  onClick={() => selectCustomer(cust)}
                  className="group flex w-full items-center justify-between border-b border-slate-100 px-3 py-2.5 text-left text-sm transition last:border-0 hover:bg-teal-50"
                >
                  <div>
                    <div className="font-semibold text-slate-900">
                      {cust.name}
                    </div>
                    <div className="text-xs text-slate-500">{cust.phone}</div>
                  </div>
                  <Check className="h-4 w-4 text-teal-600 opacity-0 transition group-hover:opacity-100" />
                </button>
              ))}

              {customerSearch.length >= 2 &&
                customerResults.length === 0 &&
                !searchingCustomer && (
                  <div className="p-3">
                    {!isCreatingCustomer ? (
                      <div className="text-center">
                        <p className="mb-3 text-xs text-slate-500">
                          Customer not found
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setIsCreatingCustomer(true);
                            if (/^\d{10}$/.test(customerSearch)) {
                              setNewPhone(customerSearch);
                              setNewName("");
                            } else {
                              setNewName(customerSearch);
                              setNewPhone("");
                            }
                          }}
                          className="flex w-full items-center justify-center gap-2 rounded-lg bg-teal-50 py-2 text-xs font-semibold text-teal-700 transition-colors hover:bg-teal-100"
                        >
                          <Plus className="h-3.5 w-3.5" /> Create customer
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2.5">
                        <div className="flex items-center justify-between">
                          <h4 className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                            Quick Create
                          </h4>
                          <button
                            type="button"
                            aria-label="Cancel customer creation"
                            onClick={() => setIsCreatingCustomer(false)}
                            className="text-slate-400 hover:text-slate-600"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <input
                          type="text"
                          placeholder="Phone Number"
                          value={newPhone}
                          onChange={(e) => setNewPhone(e.target.value)}
                          className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
                          autoFocus
                        />
                        <input
                          type="text"
                          placeholder="Customer Name"
                          value={newName}
                          onChange={(e) => setNewName(e.target.value)}
                          className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-500/20"
                        />
                        <button
                          type="button"
                          onClick={handleQuickCreate}
                          disabled={processing}
                          className="w-full rounded-lg bg-teal-600 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:opacity-50"
                        >
                          {processing ? "Creating..." : "Create and select"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
            </div>
          )}
        </div>
      )}

      {!selectedCustomer && (
        <div className="flex items-center justify-between rounded-lg bg-slate-50 px-2.5 py-2 text-xs">
          <span className="flex items-center gap-2 text-slate-600">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white text-slate-500 shadow-sm">
              <User className="h-3.5 w-3.5" />
            </span>
            <span>
              <strong className="block font-semibold text-slate-700">
                Walk-in customer
              </strong>
              <span className="text-[10px] text-slate-500">
                No credit or loyalty
              </span>
            </span>
          </span>
          <Check className="h-4 w-4 text-teal-600" />
        </div>
      )}
    </div>
  );
}
